import sv from 'date-fns/locale/sv';
import React, { useMemo } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import { useGetAllCards } from '../../../hooks/apolloHooks/useGetAllCards';
import { useGetCustomer } from '../../../hooks/apolloHooks/useGetCustomer';
import { GET_BANKCARDS_CUSTOMER_DATA_QUERY } from '../../../integrations/allkort/graphql/gql/CustomerQueries';
import { BankCardsCustomerDataResponse } from '../../../integrations/allkort/graphql/interface/Customer';
import type { CardTypeOption } from '../../views/Transactions/TransactionsTable';
import ErrorDisplay from '../ErrorDisplay';

// Register Swedish locale for DatePicker
registerLocale('sv', sv);

// Exported for use in TransactionTableList
export interface CardOption {
    value: string;
    label: string;
    type: 'smartcard' | 'creditcard' | 'bankcard';
    isCardDisabled: boolean;  // Renamed to avoid conflict with react-select's built-in isDisabled
    cardNumber: number | null;  // For matching with transaction.cardNumber (null for bank cards)
}

type TransactionFilterProps = {
    selectedCardType: CardTypeOption | null;
    onCardTypeChange: (option: CardTypeOption | null) => void;
    selectedCard: CardOption | null;
    onCardChange: (option: CardOption | null) => void;
    startDate: Date | null;
    onStartDateChange: (date: Date | null) => void;
    endDate: Date | null;
    onEndDateChange: (date: Date | null) => void;
    isCompany?: boolean;
    isDateSpanExceedingTwoYears?: boolean;
}

const TransactionFilter: React.FC<TransactionFilterProps> = ({
    selectedCardType,
    onCardTypeChange,
    selectedCard,
    onCardChange,
    startDate,
    onStartDateChange,
    endDate,
    onEndDateChange,
    isCompany = false,
    isDateSpanExceedingTwoYears = false
}) => {

    // Create stable variable objects to prevent infinite re-renders
    const bankCardsVariables = useMemo(() => ({ first: null, after: null, before: null, last: 49 }), []);

    // Fetch ALL cards with automatic pagination (up to 200 cards)
    const { cards: regularCards, loading: cardsLoading, error: cardsError } = useGetAllCards();

    // Fetch bank cards data
    const { data: bankCardsData, loading: bankCardsLoading, error: bankCardsError } = useGetCustomer<BankCardsCustomerDataResponse>(
        GET_BANKCARDS_CUSTOMER_DATA_QUERY,
        bankCardsVariables
    );

    if (cardsError || bankCardsError) {
        console.error('Failed to load cards or bank cards:', {
            cardsError: cardsError ? cardsError.message : null,
            bankCardsError: bankCardsError ? bankCardsError.message : null
        });
    }
    const creditCardLabel = isCompany ? 'Företagskort' : 'Tankkort';

    // Helper function to extract last digits from masked PAN (remove asterisks and spaces)
    const getLastDigits = (maskedPan: string | undefined): string => {
        if (!maskedPan) return '••••';
        // Remove all asterisks and trim, then get the remaining digits
        const cleaned = maskedPan.replace(/\*/g, '').trim();
        return cleaned || maskedPan;
    };

    // Get bank cards from fetched data
    const bankCards = bankCardsData?.currentCustomer?.bankCards?.nodes || [];

    // Check what card types customer has
    const hasSmartCards = regularCards.some((card: any) => card.type?.shortName === 'P');
    const hasCreditCards = regularCards.some((card: any) => card.type?.shortName === 'K');
    const hasBankCards = bankCards.length > 0;

    // Build card TYPE options (first select)
    const cardTypeOptions: CardTypeOption[] = useMemo(() => {
        const options: CardTypeOption[] = [];

        if (hasSmartCards) {
            options.push({ value: 'smartcard', label: 'Smartkort' });
        }
        if (hasCreditCards) {
            options.push({ value: 'creditcard', label: creditCardLabel });
        }
        if (hasBankCards) {
            options.push({ value: 'bankcard', label: 'Bankkort' });
        }

        return options;
    }, [hasSmartCards, hasCreditCards, hasBankCards, creditCardLabel]);

    // Build individual card options (second select) - all cards
    const cardOptions: CardOption[] = useMemo(() => {
        const options: CardOption[] = [];

        // Add regular cards (smartcards, credit cards)
        regularCards.forEach((card: any) => {
            const isSmartCard = card.type?.shortName === 'P';
            const cardType = isSmartCard ? 'smartcard' : 'creditcard';
            const cardTypeLabel = isSmartCard ? 'Smartkort' : creditCardLabel;
            const lastDigits = getLastDigits(card.maskedPan);
            const isCardDisabled = card.limit?.disabled?.isDisabled === true;
            const cardName = card.reference2?.trim();

            options.push({
                value: card.id,
                label: `${cardTypeLabel}${cardName ? `: ${cardName}` : ''}, Slutar med: ${lastDigits}`,
                type: cardType,
                isCardDisabled: isCardDisabled,
                cardNumber: card.number  // For matching with transaction.cardNumber
            });
        });

        // Add bank cards - show full masked PAN, no name (bank cards can't be disabled)
        bankCards.forEach((bankCard: any) => {
            options.push({
                value: bankCard.id,
                label: `Bankkort: ${bankCard.maskedPan}`,
                type: 'bankcard',
                isCardDisabled: false,
                cardNumber: null  // Bank card transactions have cardNumber: null
            });
        });

        // Sort: active cards first, disabled cards last
        options.sort((a, b) => {
            if (a.isCardDisabled === b.isCardDisabled) return 0;
            return a.isCardDisabled ? 1 : -1;
        });

        return options;
    }, [regularCards, bankCards, creditCardLabel]);

    // Filter card options based on selected card type
    const filteredCardOptions = useMemo(() => {
        if (!selectedCardType) {
            // No type selected - show all cards
            return cardOptions;
        }
        // Filter by selected card type
        return cardOptions.filter(card => card.type === selectedCardType.value);
    }, [cardOptions, selectedCardType]);

    // Handle card type change - clear specific card if it doesn't match new type
    const handleCardTypeChange = (option: CardTypeOption | null) => {
        onCardTypeChange(option);
        // Clear selected card if it doesn't match the new type filter
        if (option && selectedCard && selectedCard.type !== option.value) {
            onCardChange(null);
        }
    };

    // Handle specific card change
    const handleCardChange = (option: CardOption | null) => {
        onCardChange(option);
    };


    if (cardsLoading || bankCardsLoading) {
        return (
            <div className="loading-page">
                <span className="loader-zoom">
                </span>
            </div>
        );
    }
    if (cardsError) return <ErrorDisplay errorContext="Fel vid laddning av kort(transaction filter):" errorMessage={cardsError?.message} />;
    if (bankCardsError) return <ErrorDisplay errorContext="Fel vid laddning av bankkort:" errorMessage={bankCardsError?.message} />;

    // Custom styles for react-select to maintain consistent width
    const selectStyles = {
        container: (base: any) => ({
            ...base,
        }),
        control: (base: any) => ({
            ...base,
        }),
        input: (base: any) => ({
            ...base,
            width: '100%',
            '& input': {
                width: '100% !important',
            },
        }),
        valueContainer: (base: any) => ({
            ...base,
            width: '100%',
        }),
        menu: (base: any) => ({
            ...base,
            zIndex: 100,
        }),
        menuPortal: (base: any) => ({
            ...base,
            zIndex: 100,
        }),
    };

    // Custom option styles for specific card select (to show disabled cards with strikethrough)
    const cardSelectStyles = {
        ...selectStyles,
        option: (base: any, state: any) => ({
            ...base,
            textDecoration: state.data.isCardDisabled ? 'line-through' : 'none',
            color: state.data.isCardDisabled ? '#999' : base.color,
        }),
        singleValue: (base: any, state: any) => ({
            ...base,
            textDecoration: state.data.isCardDisabled ? 'line-through' : 'none',
            color: state.data.isCardDisabled ? '#999' : base.color,
        }),
    };

    // Custom format for card options to show "(Spärrat)" suffix for disabled cards
    const formatCardOptionLabel = (option: CardOption) => (
        <span>
            {option.label}
            {option.isCardDisabled && <span style={{ color: '#999', marginLeft: '8px' }}>(Spärrat)</span>}
        </span>
    );

    // Custom format for card type options to show badge with first letter
    const formatCardTypeOptionLabel = (option: CardTypeOption) => {
        // Get first letter based on card type
        const getFirstLetter = () => {
            if (option.value === 'smartcard') return 'S';
            if (option.value === 'creditcard') return isCompany ? 'F' : 'T';
            if (option.value === 'bankcard') return 'B';
            return '';
        };

        return (
            <span className="card-type-option">
                <span className={`card-type-badge card-type-badge--${option.value}${isCompany ? ' is-company' : ''}`}>
                    {getFirstLetter()}
                </span>
                <span className="card-type-label">{option.label}</span>
            </span>
        );
    };

    return (
        <div className="transaction-filter">
            <div className="transaction-filter__group">
                <label className="transaction-filter__label" htmlFor={'card-type-select'}>Korttyp</label>
                <Select
                    inputId="card-type-select"
                    options={cardTypeOptions}
                    value={selectedCardType}
                    onChange={handleCardTypeChange}
                    placeholder="Filtrera korttyp..."
                    isClearable
                    isSearchable={false}
                    openMenuOnFocus
                    className='card-type-select'
                    classNamePrefix="react-select"
                    styles={selectStyles}
                    formatOptionLabel={formatCardTypeOptionLabel}
                    noOptionsMessage={() => 'Inga matchande korttyper'}
                    name={'card-type-select'}
                />
            </div>
            <div className="transaction-filter__group">
                <label className="transaction-filter__label" htmlFor="specific-card-select">Kort</label>
                <Select
                    inputId="specific-card-select"
                    options={filteredCardOptions}
                    value={selectedCard}
                    onChange={handleCardChange}
                    placeholder="Filtrera per kort..."
                    isClearable
                    isSearchable
                    openMenuOnFocus
                    className='specific-card-select'
                    classNamePrefix="react-select"
                    styles={cardSelectStyles}
                    formatOptionLabel={formatCardOptionLabel}
                    noOptionsMessage={() => 'Inga matchande kort'}
                />
            </div>
            <div className="transaction-filter__group first">
                <label className="transaction-filter__label">Från datum</label>
                <DatePicker
                    dateFormat="yyyy-MM-dd"
                    locale="sv"
                    selected={startDate}
                    onChange={onStartDateChange}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    maxDate={endDate || new Date()}
                    className="transaction-filter__datepicker"
                    placeholderText="Välj startdatum..."
                />
                {isDateSpanExceedingTwoYears && (
                    <div className="date-error-message">
                        Maximalt datumspann är 2 år.
                    </div>
                )}
            </div>
            <div className="transaction-filter__group">
                <label className="transaction-filter__label">Till datum</label>
                <DatePicker
                    dateFormat="yyyy-MM-dd"
                    locale="sv"
                    selected={endDate}
                    onChange={onEndDateChange}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate}
                    maxDate={new Date()}
                    className="transaction-filter__datepicker"
                    placeholderText="Välj slutdatum..."
                />
            </div>
        </div>
    )
}

export default TransactionFilter
